import React, { useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { TrendingUp } from "lucide-react";

/**
 * Aggregates timeline entries by month:
 *   - avg_rating: rolling average of star ratings that occurred that month or earlier
 *   - net_pref: cumulative (favorites - dislikes - 2*never) up to and including that month
 * Yields one data point per month between the first and last entry.
 */
function bucketByMonth(entries) {
  if (!entries || entries.length === 0) return [];
  const sorted = [...entries].filter(e => e.at).sort((a,b) => a.at.localeCompare(b.at));
  const monthKey = (iso) => iso.slice(0,7); // YYYY-MM
  const firstMonth = monthKey(sorted[0].at);
  const lastMonth = monthKey(sorted[sorted.length-1].at);
  const months = [];
  let cursor = firstMonth;
  while (cursor <= lastMonth) {
    months.push(cursor);
    const [y,m] = cursor.split("-").map(Number);
    const next = m === 12 ? `${y+1}-01` : `${y}-${String(m+1).padStart(2,"0")}`;
    cursor = next;
  }
  const points = [];
  let ratingSum = 0, ratingCount = 0, netPref = 0;
  const byMonth = {};
  sorted.forEach(e => {
    const k = monthKey(e.at);
    (byMonth[k] = byMonth[k] || []).push(e);
  });
  months.forEach(mk => {
    (byMonth[mk] || []).forEach(e => {
      if (e.kind === "rating" && typeof e.stars === "number") {
        ratingSum += e.stars; ratingCount += 1;
      } else if (e.kind === "preference") {
        if (e.tag === "favorite") netPref += 1;
        else if (e.tag === "disliked") netPref -= 1;
        else if (e.tag === "never") netPref -= 2;
      }
    });
    points.push({
      month: mk,
      avg_rating: ratingCount > 0 ? Number((ratingSum / ratingCount).toFixed(2)) : null,
      net_pref: netPref,
      ratings_so_far: ratingCount,
    });
  });
  return points;
}

export default function RestaurantTrendChart({ entries }) {
  const data = useMemo(() => bucketByMonth(entries), [entries]);
  if (data.length < 2) return null; // need at least 2 months to show a trend
  const anyRatings = data.some(p => p.avg_rating != null);
  const anyPref = data.some(p => p.net_pref !== 0);
  if (!anyRatings && !anyPref) return null;
  return (
    <div data-testid="restaurant-trend-chart" className="space-y-2">
      <div className="flex items-center gap-2">
        <TrendingUp className="w-5 h-5 text-primary"/>
        <h3 className="font-serif text-xl">Taste trend</h3>
        <span className="text-xs text-muted-foreground mono">({data.length} month{data.length===1?"":"s"} of history)</span>
      </div>
      <div className="rounded-lg border p-3 bg-background" style={{height: 220}}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{top: 8, right: 12, left: 0, bottom: 8}}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))"/>
            <XAxis dataKey="month" tick={{fontSize: 10}} stroke="hsl(var(--muted-foreground))"/>
            <YAxis yAxisId="rating" domain={[0, 5]} tick={{fontSize: 10}} stroke="hsl(var(--muted-foreground))"
                   label={{ value: "Avg ★", angle: -90, position: "insideLeft", style: {fontSize: 10, fill: "hsl(var(--muted-foreground))"} }}/>
            <YAxis yAxisId="pref" orientation="right" tick={{fontSize: 10}} stroke="hsl(var(--muted-foreground))"
                   label={{ value: "Net pref", angle: 90, position: "insideRight", style: {fontSize: 10, fill: "hsl(var(--muted-foreground))"} }}/>
            <ReferenceLine yAxisId="pref" y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="2 2"/>
            <Tooltip contentStyle={{fontSize: 12, borderRadius: 8}} labelStyle={{fontWeight: 600}}/>
            <Legend wrapperStyle={{fontSize: 11}}/>
            {anyRatings && <Line yAxisId="rating" type="monotone" dataKey="avg_rating" name="Avg rating (★)"
                                  stroke="#F59E0B" strokeWidth={2} dot={{r: 3}} activeDot={{r: 5}} connectNulls/>}
            {anyPref && <Line yAxisId="pref" type="monotone" dataKey="net_pref" name="Net preference"
                               stroke="#D96B27" strokeWidth={2} dot={{r: 3}} activeDot={{r: 5}}/>}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[10px] text-muted-foreground">
        Avg rating is cumulative across all ratings so far. Net preference sums favorites (+1), dislikes (−1), never-visit (−2) across all members.
      </p>
    </div>
  );
}
